import * as React from 'react';
import { Formik, Form, Field } from 'formik';
import { ClubProgrammeBookingWindowFields } from './components/ClubProgrammeBookingWindowFields';
import {
  CLUB_PROGRAMME_SELECTION_MODE_OPTIONS,
  CLUB_PROGRAMME_PREBOOKING_ACCESS_OPTIONS
} from 'Src/consts/clubProgramme';
import { AfterSchoolClubProgramme } from 'Src/models/clubProgramme';
import { ClubProgrammeAudienceFields } from './components/ClubProgrammeAudienceFields';
import {
  ClubProgrammeFormValues,
  clubProgrammeFormSchema,
  buildClubProgrammeFormInitialValues
} from './helpers/clubProgrammeForm';
import { AppUser } from 'Src/views/App/App';
import { SchoolForm } from 'Src/models/form';
import './ClubProgrammes.css';

interface Props {
  user: AppUser;
  forms: SchoolForm[];
  initial?: Partial<AfterSchoolClubProgramme>;
  initialValues?: Partial<ClubProgrammeFormValues>;
  onCancel: () => void;
  onSubmit: (values: ClubProgrammeFormValues) => void | Promise<void>;
  submitText?: string;
  title?: string;
  subtitle?: string;
  backendOpensError?: string;
  backendClosesError?: string;
  backendFormError?: string;
  isSaving?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

const DirtyStateReporter: React.FC<{ dirty: boolean; onChange?: (dirty: boolean) => void }> = ({ dirty, onChange }) => {
  React.useEffect(() => {
    if (onChange) onChange(dirty);
  }, [dirty, onChange]);
  return null;
};

const getSelectionModeLabel = (mode: string): string => (mode === 'PRIORITY' ? 'Priority ranking' : 'Single choice');

const getVisibilityOption = (value: string): { label: string; description: string } => {
  switch (value) {
    case 'HIDE':
      return { label: 'Hidden', description: 'Do not show the programme before booking opens.' };
    case 'DRAFT':
      return { label: 'Draft', description: 'Keep the programme available only for staff preparation.' };
    default:
      return { label: 'Visible', description: 'Show the programme before booking opens without allowing booking.' };
  }
};

export const ClubProgrammeForm: React.FC<Props> = ({
  user,
  forms,
  initial,
  initialValues,
  onCancel,
  onSubmit,
  submitText,
  backendOpensError,
  backendClosesError,
  backendFormError,
  isSaving = false,
  onDirtyChange
}) => {
  const mergedInitial = buildClubProgrammeFormInitialValues(initial, initialValues);
  const programmeStatus = String(initial?.status || initialValues?.status || 'DRAFT').toUpperCase();
  const isActiveProgramme = programmeStatus === 'ACTIVE';
  const initialOpensAtRaw = initial && (initial as any).booking ? (initial as any).booking.opensAt : null;
  const initialClosesAtRaw = initial && (initial as any).booking ? (initial as any).booking.closesAt : null;
  const initialOpensAt = initialOpensAtRaw ? new Date(initialOpensAtRaw) : null;
  const initialClosesAt = initialClosesAtRaw ? new Date(initialClosesAtRaw) : null;
  const now = new Date();
  const isOpensLocked = isActiveProgramme && !!initialOpensAt && now.getTime() >= initialOpensAt.getTime();
  const isClosesLocked = isActiveProgramme && !!initialClosesAt && now.getTime() >= initialClosesAt.getTime();

  return (
    <Formik
      initialValues={mergedInitial}
      validationSchema={clubProgrammeFormSchema}
      validateOnBlur
      validateOnChange={false}
      enableReinitialize
      onSubmit={values =>
        onSubmit({
          ...values,
          name: (values.name || '').trim(),
          description: (values.description || '').trim()
        })
      }
    >
      {({ errors, touched, values, isSubmitting, submitCount, setFieldValue, dirty }) => {
        const showTopError = submitCount > 0 && Object.keys(errors).length > 0;
        const isInvalid = (field: keyof ClubProgrammeFormValues) =>
          Boolean((errors as any)[field]) && (Boolean((touched as any)[field]) || submitCount > 0);
        const isBusy = isSubmitting || isSaving;

        return (
          <Form className="cpEditorForm">
            <DirtyStateReporter dirty={dirty} onChange={onDirtyChange} />

            {showTopError ? (
              <div className="cpEditorAlert isDanger" role="alert">
                <strong>Please check the form</strong>
                <span>Some required information is missing or invalid.</span>
              </div>
            ) : null}

            {backendFormError ? (
              <div className="cpEditorAlert isDanger" role="alert">
                <strong>Unable to save programme</strong>
                <span>{backendFormError}</span>
              </div>
            ) : null}

            {isActiveProgramme ? (
              <div className="cpEditorAlert" role="note">
                <strong>Limited editing</strong>
                <span>
                  Active programmes keep their audience, selection and visibility settings. Booking dates can be changed
                  only until their current cut-off has passed.
                </span>
              </div>
            ) : null}

            <section className="cpEditorSection">
              <header>
                <span>1</span>
                <div>
                  <h2>Programme details</h2>
                  <p>Name the programme and add context for staff and bookers.</p>
                </div>
              </header>
              <div className="cpEditorGrid">
                <div className="cpEditorField cpEditorFieldWide">
                  <label htmlFor="club-programme-name">
                    Programme name <span>*</span>
                  </label>
                  <Field
                    id="club-programme-name"
                    name="name"
                    type="text"
                    placeholder="e.g. Spring term after-school clubs"
                    className={isInvalid('name') ? 'isInvalid' : ''}
                    autoFocus
                  />
                  {isInvalid('name') ? <div className="cpEditorError">{(errors as any).name}</div> : null}
                </div>
                <div className="cpEditorField cpEditorFieldWide">
                  <label htmlFor="club-programme-description">Description</label>
                  <Field
                    id="club-programme-description"
                    as="textarea"
                    name="description"
                    rows={4}
                    placeholder="Optional description shown to staff, parents and students."
                    className={isInvalid('description') ? 'isInvalid' : ''}
                  />
                  {isInvalid('description') ? <div className="cpEditorError">{(errors as any).description}</div> : null}
                </div>
              </div>
            </section>

            <section className="cpEditorSection">
              <header>
                <span>2</span>
                <div>
                  <h2>Booking and selection</h2>
                  <p>Control when booking is available and how families choose clubs.</p>
                </div>
              </header>
              <div className="cpEditorGrid">
                <div className="cpEditorField cpEditorFieldWide">
                  <label>Selection mode</label>
                  <div className="cpEditorChoiceCards" role="radiogroup" aria-label="Selection mode">
                    {CLUB_PROGRAMME_SELECTION_MODE_OPTIONS.map(mode => {
                      const selected = values.selectionMode === mode;
                      return (
                        <button
                          key={mode}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          className={`cpEditorChoiceCard ${selected ? 'isSelected' : ''}`}
                          disabled={isActiveProgramme}
                          onClick={() => setFieldValue('selectionMode', mode)}
                        >
                          <strong>{getSelectionModeLabel(mode)}</strong>
                          <span>
                            {mode === 'PRIORITY'
                              ? 'Bookers rank several preferred clubs for each day.'
                              : 'Bookers choose one club for each overlapping time.'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {values.selectionMode === 'PRIORITY' ? (
                  <div className="cpEditorField cpEditorFieldWide">
                    <label>
                      Maximum priorities per day <span>*</span>
                    </label>
                    <div className="cpEditorChips" role="radiogroup" aria-label="Maximum priorities per day">
                      {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(value => (
                        <button
                          key={value}
                          type="button"
                          role="radio"
                          aria-checked={Number(values.priorityMax) === value}
                          className={`cpEditorChip ${Number(values.priorityMax) === value ? 'isSelected' : ''}`}
                          disabled={isActiveProgramme}
                          onClick={() => setFieldValue('priorityMax', value)}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                    {isInvalid('priorityMax') ? (
                      <div className="cpEditorError">{(errors as any).priorityMax}</div>
                    ) : null}
                  </div>
                ) : null}

                <div className="cpEditorField cpEditorFieldWide">
                  <label>Visibility before booking opens</label>
                  <div
                    className="cpEditorChoiceCards cpEditorChoiceCardsThree"
                    role="radiogroup"
                    aria-label="Visibility"
                  >
                    {CLUB_PROGRAMME_PREBOOKING_ACCESS_OPTIONS.map(value => {
                      const option = getVisibilityOption(value);
                      const selected = values.preBookingAccess === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          className={`cpEditorChoiceCard ${selected ? 'isSelected' : ''}`}
                          disabled={isActiveProgramme}
                          onClick={() => setFieldValue('preBookingAccess', value)}
                        >
                          <strong>{option.label}</strong>
                          <span>{option.description}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="cpEditorField cpEditorFieldWide">
                  <label>Booking window</label>
                  <ClubProgrammeBookingWindowFields
                    bookingOpensAt={values.bookingOpensAt}
                    bookingClosesAt={values.bookingClosesAt}
                    minDate={new Date()}
                    opensError={backendOpensError || (errors as any).bookingOpensAt}
                    closesError={backendClosesError || (errors as any).bookingClosesAt}
                    isOpensInvalid={Boolean(backendOpensError) || isInvalid('bookingOpensAt')}
                    isClosesInvalid={Boolean(backendClosesError) || isInvalid('bookingClosesAt')}
                    isOpensDisabled={isOpensLocked}
                    isClosesDisabled={isClosesLocked}
                    onChangeOpens={value => setFieldValue('bookingOpensAt', value)}
                    onChangeCloses={value => setFieldValue('bookingClosesAt', value)}
                  />
                </div>

                <div className="cpEditorField cpEditorFieldWide">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={Boolean(values.allowChangesAfterSubmit)}
                    className={`cpEditorSwitchRow ${values.allowChangesAfterSubmit ? 'isOn' : ''}`}
                    disabled={isActiveProgramme}
                    onClick={() => setFieldValue('allowChangesAfterSubmit', !values.allowChangesAfterSubmit)}
                  >
                    <span className="cpEditorSwitch" aria-hidden="true">
                      <i />
                    </span>
                    <span>
                      <strong>Allow changes after submission</strong>
                      <small>Bookers can update their choices until booking closes.</small>
                    </span>
                  </button>
                </div>
              </div>
            </section>

            <section className="cpEditorSection">
              <header>
                <span>3</span>
                <div>
                  <h2>Audience and notifications</h2>
                  <p>Choose eligible students and who receives programme notifications.</p>
                </div>
              </header>
              <ClubProgrammeAudienceFields
                user={user}
                forms={forms}
                programme={initial}
                audienceAges={values.audienceAges}
                audienceGenders={values.audienceGenders}
                audienceInvitations={values.audienceInvitations}
                setFieldValue={setFieldValue}
                agesError={(errors as any).audienceAges}
                gendersError={(errors as any).audienceGenders}
                invitationsError={(errors as any).audienceInvitations}
                isAgesInvalid={isInvalid('audienceAges')}
                isGendersInvalid={isInvalid('audienceGenders')}
                isInvitationsInvalid={isInvalid('audienceInvitations')}
                disabled={isActiveProgramme}
              />
            </section>

            <footer className="cpEditorActions">
              <span className={dirty ? 'isDirty' : ''}>{dirty ? 'Unsaved changes' : 'No pending changes'}</span>
              <button type="button" className="cpEditorButton isSecondary" disabled={isBusy} onClick={onCancel}>
                Cancel
              </button>
              <button type="submit" className="cpEditorButton isPrimary" disabled={isBusy}>
                {isBusy ? 'Saving…' : submitText || (initial?.id ? 'Save changes' : 'Create programme')}
              </button>
            </footer>
          </Form>
        );
      }}
    </Formik>
  );
};

ClubProgrammeForm.displayName = 'ClubProgrammeForm';
